<template>
  <Loading v-if="showLoader" />
  <ViewComponent
    v-if="readyToRenderViewComponent"
    ref="componentRef"
    v-bind="pageData.pageName !== CHECKOUT_PAGE && pageData.pageName !== SAVE_THE_SALE ? {
      pageDataLoaded,
      dataRevalidated
    } : { pageDataLoaded }"
    @viewMounted="onViewMounted"
    @viewRendered="viewRendered"
  />
</template>

<script setup>
import {
  computed, defineAsyncComponent, onBeforeMount, provide, ref, watch, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import useLoadUtag from '@common-vue/bag-checkout-common-consumable/src/composables/useLoadUtag';
import { SET_COOKIE_DATA, SET_DEVICE_TYPE } from '@common-vue/bag-checkout-common-consumable/src/types/mutations';
import { BAG_GUID, USER_ID, USER_GUID } from '@common-vue/bag-checkout-common-consumable/src/constants';
import getGlobalProvides from '@common-vue/bag-checkout-common-consumable/src/utils/getGlobalProvides';
import supportsWebp from '@common-vue/bag-checkout-common-consumable/src/utils/webpSupportDetection';
import Loading from '@common-vue/bag-checkout-common-consumable/src/components/Loading';
import { SessionStorage } from '@common-vue/bag-checkout-common-consumable/src/utils/storageUtil';
import useRouteModules from '@composables/useRouteModules';
import useCacheUpdates from '@composables/useCacheUpdates';
import useServiceWorker from '@composables/useServiceWorker';
import { SET_INITIAL_PAGE_DATA } from '@types/actions';
import { IS_INITIAL_DATA_SET } from '@types/getters';
import { fetchPageData, fetchApplePay, fetchSTSData } from '@services/PageDataService';
import getRoutes, { getInternalRoutePath } from './internal_routes';

const BRAND_NAME = (process.env.ENTRY_POINT === 'bcom') ? 'Bloomingdales' : 'Macys';
const CHECKOUT_PAGE = 'Checkout';
const SAVE_THE_SALE = 'Save the Sale';
const PAGE_REFRESHED_ON_NETWORK_ERROR = 'RefreshPageOnNetworkError';
const isClient = process.env.APP_ENV === 'client';
let isPrerender = false;
let logLevelParam = null;

if (isClient) {
  const urlSearch = new URLSearchParams(window.location.search);
  isPrerender = urlSearch.get('prerender') === 'true';
  logLevelParam = urlSearch.get('logLevel');
}
const globalProvides = getGlobalProvides();
const { cookies, logger } = globalProvides;
const readyToRenderViewComponent = ref(false);
let ViewComponent = () => null;

const store = useStore();
let routeStoreFactory;
let handleResponseSessionHeaders;
const loagUtag = useLoadUtag();

const viewMounted = ref(false);
let isViewRenderedCallbackDone = false;
let isViewRenderedCallbackTriggered = false;
const pageDataLoaded = ref(false);
const componentRef = ref(null);
const dataRevalidated = computed(() => store.state.revalidationState?.value);
const pageData = computed(() => store.state.pageData || {});
let isUserChanged = false;
let hasNoUpdatesOnUserChangeRevalidate;
const hasNoUpdatesOnRevalidate = () => {
  /**
    * Get hasUpdates from Service worker which is set in store revalidationState
    * hasUpdates can be true, false or undefined
    * true - when there is change in data - in this case SET_INITIAL_PAGE_DATA will be already called by useCacheUpdates
    * BELOW TWO ARE THE ONES WE ARE HANDLING HERE
    * false - when user has latest SW and there is no update in DATA (which means SET_INITIAL_PAGE_DATA is not called)
    * undefined - when user has OLD SW and there is no update in DATA (which means SET_INITIAL_PAGE_DATA is not called)
  */
  const { hasUpdates } = store.state.revalidationState || {};
  return hasUpdates === false || (hasUpdates === undefined && !store.getters[IS_INITIAL_DATA_SET]);
};
const showLoader = computed(() => !isPrerender && (store.state.showLoader
  // if the dataRevalidated is undefined, then we do not need to wait for the re-validation
  || dataRevalidated.value === false
));

/**
 * Call onReady of Component
 * This should not be called when page is loaded from cache and there is some update
 * @param { Object } { isDataRevalidated : Boolean }
 * This will be called in below three scenarios
 * 1. When data is fresh from API and above the fold has rendered
 * 2. When data is from Cache and revalidated response is different from the cached response
 *   In case 2 , it will get called only after latest API response is received
 * 3. where data is loaded from cache and userId, userGuid or bagGuid
      cookies are different from the values in the cached response
   *  In case 3, if the revalidated response is same as the cached response then this call is made after above the fold DOM is rendered

 */
const callComponentOnReady = ({ isDataRevalidated } = {}) => {
  try {
    componentRef.value?.onReady?.({ isDataRevalidated });
  } catch (error) {
    logger.error('Error while calling component\'s onReady', error);
  }
};

/**
 * Call SET_COOKIE_DATA to set cookies from browser cookie storage to state
 */
function setBrowserCookiesToState() {
  store.commit(SET_COOKIE_DATA, cookies);
}
/**
 * Called when visibilitychange event is fired and browser tab becomes visible to user
 */
function tabVisibilityChangeCallback() {
  // If user has moved from other tab to present one, then call SET_COOKIE_DATA to reset cookies from storage to state
  if (document.visibilityState === 'visible') {
    setBrowserCookiesToState();
  }
}

/**
 * Called when focus on window element is received ( when user moved from other tab to current tab )
 */
function windowFocusChangeCallback() {
  // call SET_COOKIE_DATA to reset cookies from storage to state
  setBrowserCookiesToState();
}

/**
 * Listener for tab change using window.focus/document.visibilitychange events
 */
function addListenersForTabChange() {
  try {
    // If VisibiltyState API is supported, then add listener to visibilitychange
    if (document.visibilityState !== undefined && document.onvisibilitychange !== undefined) {
      document.addEventListener('visibilitychange', tabVisibilityChangeCallback);
    } else {
      window.addEventListener('focus', windowFocusChangeCallback);
      logger.info('VisibiltyState API is not supported, hence using window.onfocus to refresh cookies', window.navigator);
    }
  } catch (error) {
    logger.error('Error while setting up tab change listener', error);
  }
}
function viewRendered() {
  // To let viewMounted know that already viewRendered tried to trigger
  isViewRenderedCallbackTriggered = true;
  // If not viewMounted , then do no execute this
  if (!viewMounted.value) return;

  // Load Utag Library
  loagUtag();
  /**
   * Call component onReady for two scenarios here
   * 1. Call component's onReady function when data is fresh and pageDataHasLoaded
   * 2. Call component's onReady function when userHasChanged on load and there is no updates in SW on revalidate
   */
  if (dataRevalidated.value === undefined || hasNoUpdatesOnUserChangeRevalidate) {
    callComponentOnReady({ isDataRevalidated: !!dataRevalidated.value });
  }

  // Register the service worker
  useServiceWorker();

  // To let viewMounted know that already viewRendered is done
  isViewRenderedCallbackDone = true;
}

function onViewMounted() {
  viewMounted.value = true;

  /**
     * if ViewComponent already tried to call viewRendered, but viewRendered callback was not done fully
     *  as viewMounted was not completed at that time - then call view rendered from here
     *
     * Both variables are needed, Assume a scenario where ViewComponent is mounted but component decides to
     *  trigger viewRendered later, in this scenario, ViewComponent does not want to call viewRendered after mouunted,
     *
     * if code has only !isViewRenderedCallbackDone check, then for above case as well viewRendered will be triggered
     *  even though viewComponent did not want to triggere viewRendered, at that point.
     */
  if (isViewRenderedCallbackTriggered && !isViewRenderedCallbackDone) {
    viewRendered();
  }
}

/**
* Converts an Error into a string with a configurable stack depth.
* @param {Error|any} err - The error object.
* @param {number} maxStackFrames - Number of stack frames to keep (default 5).
* @returns {string} Formatted error chain string.
*/
function stringifyErrorChain(err, maxStackFrames = 5) {
  if (!(err instanceof Error)) return String(err || '');
  let { stack } = err;
  const {
    name, message, cause,
  } = err;
  const nameAndMessage = `${name}: ${message}`;
  if (!stack) return nameAndMessage;

  // 1. Normalization: Ensure name/message are at the top (for Firefox/Safari)
  if (stack && !stack.startsWith?.(name)) {
    stack = `${nameAndMessage}\n${stack}`;
  }

  // 2. Truncation: Limit the number of frames
  if (stack) {
    const lines = stack.split('\n');
    // The first line is usually the name/message; we take that + maxStackFrames
    const truncatedStack = lines.slice(0, maxStackFrames + 1).join('\n');
    stack = lines.length > maxStackFrames + 1
      ? `${truncatedStack}\n    ... (${lines.length - (maxStackFrames + 1)} more frames)`
      : truncatedStack;
  }

  let result = stack || nameAndMessage;

  // 3. Recursion: Process the cause chain
  if (cause) {
    result += `\n\n[Cause]: ${stringifyErrorChain(cause, maxStackFrames)}`;
  }

  return result;
}

let oopsTrackerId = '';
/**
/**
 * A common error handler that logs an error and sends a tracking request.
 * @param {Error|any} error - The error object to log and track.
 * @param {string} contextMessage - A message describing the context of the error.
 * @param {string} thrownErrorType - The type of error for the tracking GIF.
 */
function logErrorWithOopsTracker(contextMessage, error, thrownErrorType = 'InitialPagedataError') {
  let reason;
  try {
    reason = stringifyErrorChain(error);
  } catch (err) {
    logger.error('Error while stringifying error chain', err);
    reason = String(error || '');
  }

  logger.error(contextMessage, error);
  const baseUrl = `/web20/assets/img/oopsTracker/oopsTracker.gif?thrownerror=${thrownErrorType}&message=${encodeURIComponent(contextMessage)}&locationUrl=${encodeURIComponent(document.location.href)}&id=${oopsTrackerId}&reason=`;
  const maxReasonLength = 1000 - baseUrl.length;
  if (reason.length > maxReasonLength) reason = reason.substring(0, maxReasonLength);
  fetch(`${baseUrl}${encodeURIComponent(reason)}`);
}

/**
 * Reloads the page once on error to recover from any network issues.
 * Uses SessionStorage to reload only once for the session
 */
function reloadPageOnceOnNetworkError(error) {
  try {
    if (SessionStorage.getItem(PAGE_REFRESHED_ON_NETWORK_ERROR) !== 'true') {
      logErrorWithOopsTracker('Refreshing the page on network error', error);
      SessionStorage.setItem(PAGE_REFRESHED_ON_NETWORK_ERROR, 'true');
      window.location.reload();
    }
  } catch (e) {
    logErrorWithOopsTracker('Failed to refresh the page on network error', e);
  }
}

if (isPrerender) {
  // Register the service worker
  useServiceWorker();
} else {
  store.commit('showLoader');
  if (isClient) {
    // Test for webp support ASAP
    supportsWebp();

    const routePath = getInternalRoutePath();
    const route = getRoutes()
      .find((routeDetails) => routeDetails.paths.includes(routePath))
                  || {};

    routeStoreFactory = route.store;
    const { component } = route;
    handleResponseSessionHeaders = route.handleResponseSessionHeaders;
    oopsTrackerId = route.oopsTrackerId || `${Date.now() }-${ Math.floor(Math.random() * 100000)}`;

    if (component) {
      ViewComponent = defineAsyncComponent(component);
    }
  }

  Object.entries(globalProvides).forEach(([key, value]) => provide(key, value));
  logger.setupLogLevels(logLevelParam || store.state.envProps.logLevel || 'warn');

  onBeforeMount(async () => {
    // TODO: MEDIUM when we force the user to refresh the page,
    // then disable the following condition
    if (history.scrollRestoration) { // eslint-disable-line no-restricted-globals
      history.scrollRestoration = 'manual'; // eslint-disable-line no-restricted-globals
    }
    let unwatch = watch(
      () => viewMounted.value && pageDataLoaded.value,
      (isPageDoneLoading) => {
        if (isPageDoneLoading) {
          store.commit('hideLoader');
          unwatch();
          unwatch = null;
        }
      },
    );

    const { pageName, dataUrl } = pageData.value;
    let pageDataApiTimeout = store.state.envProps?.pageDataApiTimeout;
    const checkoutPageDataApiTimeout = store.state.envProps?.checkoutPageDataApiTimeout;
    let isCheckoutApplePayConfirmation;
    const sessionApplePayOrderData = SessionStorage.getItem('applePayOrderData');
    if (pageName === CHECKOUT_PAGE) {
      pageDataApiTimeout = checkoutPageDataApiTimeout || 75000;
      const params = new URLSearchParams(window.location.search);
      isCheckoutApplePayConfirmation = params.get('showOrderConfirmation') === 'true' && sessionApplePayOrderData;
    }

    const INITIAL_PAGE_DATA_START_MARK = 'initialDataPromise-start';
    const INITIAL_PAGE_DATA_END_MARK = 'initialDataPromise-end';
    const INITIAL_PAGE_DATA_DURATION_MEASURE = 'initialDataPromise-duration';

    performance.mark(INITIAL_PAGE_DATA_START_MARK);
    let pageDataPromise;
    if (isCheckoutApplePayConfirmation) {
      pageDataPromise = fetchApplePay({
        pageDataApiTimeout,
        payload: JSON.parse(sessionApplePayOrderData),
      });
    } else if (pageName === SAVE_THE_SALE) {
      pageDataPromise = fetchSTSData({ dataUrl, pageDataApiTimeout });
    } else {
      pageDataPromise = fetchPageData({ dataUrl, pageDataApiTimeout });
    }

    let measured = false;
    const measureAndLogPromise = (status) => {
      if (measured) return;
      measured = true;
      try {
        performance.mark(INITIAL_PAGE_DATA_END_MARK);
        performance.measure(
          INITIAL_PAGE_DATA_DURATION_MEASURE,
          INITIAL_PAGE_DATA_START_MARK,
          INITIAL_PAGE_DATA_END_MARK,
        );
        const [measure] = performance.getEntriesByName(INITIAL_PAGE_DATA_DURATION_MEASURE, 'measure');
        if (measure && measure.duration > 1500) {
          logger.error(`Initial Page Data promise ${status} in ${measure.duration}ms. IsCheckoutApplePayConfirmation: ${!!isCheckoutApplePayConfirmation}`);
          fetch(`/web20/assets/img/oopsTracker/oopsTracker.gif?thrownerror=DelayedAPIResponseTime&duration=${measure.duration}ms&status=${status}&applePayConfirmation=${!!isCheckoutApplePayConfirmation}&locationUrl=${encodeURIComponent(document.location.href)}&id=${oopsTrackerId}`);
        }
      } finally {
        performance.clearMarks(INITIAL_PAGE_DATA_START_MARK);
        performance.clearMarks(INITIAL_PAGE_DATA_END_MARK);
        performance.clearMeasures(INITIAL_PAGE_DATA_DURATION_MEASURE);
      }
    };

    const initialDataPromise = pageDataPromise.then((response) => {
      measureAndLogPromise('resolved');
      try {
        if (pageName === CHECKOUT_PAGE) {
          const { headers, data } = response;
          const { metaInfo } = data?.order || {};
          const { killSwitches } = metaInfo || {};
          const { isStartCheckoutPostCallEnabled } = killSwitches || {};
          const { cxoForceStartCheckoutPostCall } = store.state.envProps || {};
          /**
           * https://macystech.atlassian.net/browse/DCO-5966
           *
           * If UI Code cannot find the BE KS isStartCheckoutPostCallEnabled, then, look for this new UI env Config.
           * If this config value is true, make POST Call else make get call.
           *  (This is just to have backward compatibility - as UI config will be true by default,
           * it won’t run into else block unless override to false is done through DNR).
           *
           * Keeping this backward compatibility in code as we run into this scenario only when Timeout happens
           *   and BE says to redirect to start checkout.
           *
           * TODO: HIGH Once we test POST calls completely in production
           *  then we can create a story to cleanup this new UI config and remove the code which uses get call.
           */
          const responseAfterHandlingSessionHeaders = handleResponseSessionHeaders(headers, {
            isStartCheckoutPostCallEnabled:
            isStartCheckoutPostCallEnabled || cxoForceStartCheckoutPostCall,
          });
          // Only if some response is present, then return the same
          if (responseAfterHandlingSessionHeaders) {
            return responseAfterHandlingSessionHeaders;
          }
        }
      } catch (error) {
        logErrorWithOopsTracker('Error thrown while redirecting', error);
      }
      return response;
    })
      .catch((e) => {
        const { refreshOnceOnNetworkErrorEnabled } = store.state.envProps || {};
        if (refreshOnceOnNetworkErrorEnabled
              && (e.message?.includes?.('Failed to fetch') || e.message?.includes?.('Load failed'))
              && pageName === CHECKOUT_PAGE) {
          reloadPageOnceOnNetworkError(e);
        }
        logErrorWithOopsTracker('Error while fetching the page data:', e);
        measureAndLogPromise('rejected');
      });
    const routeModulesPromise = useRouteModules(store, routeStoreFactory);
    /*
      Because we are preloading the dataUrl, we first have to consume that preloaded call
      Hence we need to fire the fetchPageData call first before we could start listening for any cache updates
    */
    let data = {};
    let headers;
    setBrowserCookiesToState();
    try {
      // this line will dynamically load the page level store modules in parallel with the Initial Data API call
      // and will wait for both these actions to finish
      const [{ value: response = {} }] = await Promise.allSettled([initialDataPromise, routeModulesPromise]);
      ({ data = {}, headers } = response);
      if (headers?.get('X-Is-Validating') === 'true') { // check header here
        const browserCookiesObject = cookies.parse() || {};
        const userId = browserCookiesObject[USER_ID];
        const userGuid = browserCookiesObject[USER_GUID];
        const bagGuid = browserCookiesObject[BAG_GUID];
        const { userId: metaUserId, userGuid: metaUserGuid, bagGuid: metaBagGuid } = data.bag?.meta || {};
        // we need to worry about userChanged scenario only when we have the cached data and it will need to be revalidated
        isUserChanged = (metaUserId && userId !== metaUserId) || (metaUserGuid && userGuid !== metaUserGuid)
        || (metaBagGuid && bagGuid !== metaBagGuid);

        store.commit('dataRevalidationState', { value: false });

        try {
          // Remove unavailable items message and unavailable items from bag data when bag data is loaded from cache
          // RUA message should be shown only once to user , hence removing this if data is loaded from cache
          delete data.bag.sections.removedItems;
        } catch (_) {
          // ignore errors intentionally -  errors here can happen when sections or bag is undefined
        }

        let stopRevalidatedWatch = watch(
          // Need to wait until the view is mounted
          () => viewMounted.value && dataRevalidated.value,
          (isDataRevalidated) => {
            /*
              ViewComponent's onReady needs to be called when either "isDataRevalidated === undefined || isDataRevalidated === true".
              We will not call onReady when "isDataRevalidated === false"
              "isDataRevalidated === true" is covered here
              calling callComponentOnReady for "isDataRevalidated === undefined" scenario is covered in viewRendered function above
            */
            if (isDataRevalidated) {
              /**
               * call SET_INITIAL_PAGE_DATA when user had changed and revalidated response is same as the cached response
               *  (when response is different, then useCacheUpdates file will call SET_INITIAL_PAGE_DATA with the updated data)
               * userChange happens when userId, userGuid or bagGuid in cookies is different from the cached response
                THIS IS EDGE CASE SCENARIO WHICH IDEALLY WONT HAPPEN BUT ITS POSSIBLE IT CAN HAPPEN
              */

              // If user has changed and control reaches here , then pageDataLoaded should be set to true
              // for both has updates and not has updates scenarios
              if (isUserChanged) {
                hasNoUpdatesOnUserChangeRevalidate = hasNoUpdatesOnRevalidate();
                pageDataLoaded.value = true;
              }
              // If user has changed and there is no update
              if (hasNoUpdatesOnUserChangeRevalidate) {
                // Make sure to call SET_INITIAL_PAGE_DATA so that user is not stuck with empty page forever
                store.dispatch(SET_INITIAL_PAGE_DATA, { pageName, data });
                // For this scenario callComponentOnReady should be called after view has rendered (refer viewRendered function above)
              } else {
                // Call component's onReady function when data is revalidated and has updates or user has not changed
                callComponentOnReady({ isDataRevalidated: true });
              }
              stopRevalidatedWatch();
              stopRevalidatedWatch = null;
            }
          },
        );
      }
    } catch (error) {
      logErrorWithOopsTracker('App onBeforeMount error', error);
    } finally {
      store.commit(SET_DEVICE_TYPE, (headers?.get(`X-${BRAND_NAME}-Device`) || 'PC').toUpperCase());
      // Call SET_INITIAL_PAGE_DATA only when user data is not changed
      if (!isUserChanged) {
        pageDataLoaded.value = true;
        // passing isApplePayOrderConfirmation for Apple Pay orders only, to be used in checkout OCP
        if (isCheckoutApplePayConfirmation) {
          data.isApplePayOrderConfirmation = true;
        }
        store.dispatch(SET_INITIAL_PAGE_DATA, { pageName, data });
        if (pageName === CHECKOUT_PAGE && !data.order) {
          logErrorWithOopsTracker('order is not present in checkout page data', undefined, 'ErrorPageDisplayed');
        }
      }
    }

    /*
        Now that we have consumed the preloaded AJAX call data
        we should start listening to the cacheUpdateEvents
        and refresh the data, as needed
    */
    useCacheUpdates(store, pageName, dataUrl);
    readyToRenderViewComponent.value = true;
    // add listeners for tab change
    addListenersForTabChange();
  });

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', tabVisibilityChangeCallback);
    window.removeEventListener('focus', windowFocusChangeCallback);
  });
}
</script>
